import React, { createContext, useContext, useEffect, useRef } from "react";
import { Flex, notification, Typography } from "antd";

import { NOTIFICATION_HEADERS, NOTIFICATION_TYPES, NOTIFICATION_TYPE_ENUM, NotificationType } from "interfaces";
import { getIcon } from "./getIcon";

import styles from "./notification.module.css";
import { ReloadOutlined } from "@ant-design/icons";
import { CustomButton } from "ui";

const APP_VERS_POLLING_INTERVAL = 10 * 60 * 1000; // 10 minutes

export type NotificationProps = {
  message?: React.ReactNode;
  description?: string;
  type: NotificationType;
};

interface NotificationContextType {
  openNotification: (props: NotificationProps) => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const useNotificationContext = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotificationContext must be used within a NotificationProvider");
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [api, contextHolder] = notification.useNotification();

  const previousVersionRef = useRef<string | null>(null);
  const versionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const notificationShownRef = useRef<boolean>(false);

  const openNotification = ({ message, description, type }: NotificationProps) => {
    api.open({
      message: message ?? NOTIFICATION_HEADERS[type],
      type: NOTIFICATION_TYPES[type],
      description: description ?? undefined,
      duration: type === NOTIFICATION_TYPE_ENUM.NOTICE ? 15 : 5,
      showProgress: true,
      pauseOnHover: true,
      className: styles.notificationBlock,
      icon: getIcon(type),
    });
  };

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const response = await fetch("/build.json");
        const data = await response.json();
        const currentVersion = data.version;

        if (previousVersionRef.current === null) {
          previousVersionRef.current = currentVersion;
        } else if (previousVersionRef.current !== currentVersion && !notificationShownRef.current) {
          notificationShownRef.current = true;
          api.info({
            message: `Доступна новая версия платформы: ${currentVersion}`,
            description: (
              <Flex vertical gap={"middle"}>
                <Typography.Text className={`margin-bottom-md`} style={{ color: "#ffffff" }}>
                  Для получения обновлений перезагрузите страницу.
                </Typography.Text>

                <CustomButton icon={<ReloadOutlined />} onClick={() => window.location.reload()} size="middle">
                  Обновить страницу
                </CustomButton>
              </Flex>
            ),
            duration: 0,
            className: styles.notificationBlock,
            icon: getIcon(NOTIFICATION_TYPE_ENUM.INFO),
            placement: "topRight",
            onClose: () => {
              notificationShownRef.current = false;
            },
          });
        }
      } catch (error) {
        console.error("Ошибка при проверке версии:", error);
      }
    };

    checkVersion();

    versionCheckIntervalRef.current = setInterval(checkVersion, APP_VERS_POLLING_INTERVAL);

    return () => {
      if (versionCheckIntervalRef.current) {
        clearInterval(versionCheckIntervalRef.current);
      }
    };
  }, [api]);

  return (
    <NotificationContext.Provider value={{ openNotification }}>
      <>
        {contextHolder}
        {children}
      </>
    </NotificationContext.Provider>
  );
};
